import { Phone, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import {
  getCampusSafetyContact,
  LEGAL_CONTACT,
  NATIONAL_EMERGENCY,
} from '@makhe/shared';

interface Props {
  buyerInstitution?: string | null;
  sellerInstitution?: string | null;
  onReport?: () => void;
  bookingId?: string;
}

function telHref(number: string) {
  return `tel:${number.replace(/\s/g, '')}`;
}

function formatPhone(number: string) {
  const digits = number.replace(/\D/g, '');
  if (digits.length === 10 && digits.startsWith('0')) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return number;
}

function PhoneLink({ number, label }: { number: string; label?: string }) {
  return (
    <a
      href={telHref(number)}
      className="inline-flex items-center gap-1 text-makhe-primary underline"
    >
      <Phone size={12} />
      {label ? `${label}: ` : ''}
      {formatPhone(number)}
    </a>
  );
}

export function BookingSafetyPanel({
  buyerInstitution,
  sellerInstitution,
  onReport,
  bookingId,
}: Props) {
  const contacts = [
    getCampusSafetyContact(buyerInstitution),
    getCampusSafetyContact(sellerInstitution),
  ].filter((c, i, arr) => c && arr.findIndex((x) => x?.institution === c.institution) === i);

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <h3 className="font-display text-sm font-semibold text-makhe-ink">Safety & emergencies</h3>
            <p className="mt-1 text-xs text-makhe-muted">
              If you feel unsafe, leave the situation and use the contacts below. Off-campus
              residences may be outside campus security coverage.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {onReport && (
              <Button type="button" variant="secondary" size="sm" onClick={onReport}>
                Report on Makhe
              </Button>
            )}
            <a href={telHref(NATIONAL_EMERGENCY.saps)}>
              <Button type="button" variant="secondary" size="sm">
                <Phone size={14} /> SAPS {NATIONAL_EMERGENCY.saps}
              </Button>
            </a>
            <a href={telHref(NATIONAL_EMERGENCY.gbvHelpline)}>
              <Button type="button" variant="secondary" size="sm">
                GBV helpline
              </Button>
            </a>
          </div>

          {contacts.map((c) => (
            <div key={c!.institution} className="rounded-lg bg-makhe-bg/60 p-3 text-xs">
              <p className="font-medium text-makhe-ink">{c!.institution}</p>

              {c!.noDedicatedLine && !c!.protectionServices ? (
                <p className="mt-1 text-makhe-muted">{c!.noDedicatedLineNote}</p>
              ) : (
                <div className="mt-1 space-y-1">
                  <PhoneLink
                    number={c!.protectionServices}
                    label={c!.protectionLabel}
                  />
                  {c!.tollFree && (
                    <div>
                      <PhoneLink number={c!.tollFree} label="Toll-free" />
                    </div>
                  )}
                  {c!.lines && c!.lines.length > 1 && (
                    <ul className="space-y-0.5 pl-1 text-makhe-muted">
                      {c!.lines
                        .filter((line) => line.number !== c!.protectionServices)
                        .map((line) => (
                          <li key={`${line.label}-${line.number}`}>
                            <PhoneLink number={line.number} label={line.label} />
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}

              {c!.noDedicatedLineNote && c!.protectionServices && (
                <p className="mt-1 text-makhe-muted/90">{c!.noDedicatedLineNote}</p>
              )}
              {c!.escortInfo && <p className="mt-1 text-makhe-muted">{c!.escortInfo}</p>}
              {c!.safetyApp && (
                <p className="mt-1 text-makhe-muted">
                  {c!.safetyApp}: {c!.safetyAppNote}
                </p>
              )}
              {c!.offCampusNote && (
                <p className="mt-1 text-makhe-muted/80">{c!.offCampusNote}</p>
              )}
            </div>
          ))}

          <a
            href={`mailto:${LEGAL_CONTACT.email}`}
            className="inline-flex items-center gap-1 text-xs text-makhe-muted underline"
          >
            Makhe support: {LEGAL_CONTACT.email}
            <ExternalLink size={12} />
          </a>
          <Link
            to={
              bookingId
                ? `/profile/campus-control?bookingId=${bookingId}`
                : '/profile/campus-control'
            }
            className="block text-xs text-makhe-primary underline"
          >
            Campus Control
          </Link>
        </div>
      </div>
    </div>
  );
}
